// @ts-strict-ignore
import {
	authorize,
	authorizationRoles,
	checkForMarketAnalyticsAccess,
	checkForSiteSelectorAccess,
	checkForSupplierAnalyticsAccess
} from "app/utils/authorizationUtils";
import urlEnums from "app/utils/enum/urlEnums";
import { userRightsEnum } from "app/utils/enum/UserRights";
import { resolveConstant } from "./enum/ResolveProjectTypesEnum";

interface NavLinksInterface {
	label: string;
	to: string | { pathname: string };
	subLinks?: {
		label: string;
		to: string | { pathname: string; target: string };
		target?: string;
		rel?: string;
	}[];
	toggled?: boolean;
}

const getDashboardContent = () => {
	const dashboardContent = {
		label: "Home",
		to: "/dashboard"
	};

	return dashboardContent;
};

const getAdminContent = ({
	isSuperDev,
	isSuperAdmin,
	isDcbCompanyAdmin,
	isExportsPageFeatureGateEnabledStatsig
}: {
	isSuperDev: boolean;
	isSuperAdmin: boolean;
	isDcbCompanyAdmin: boolean;
	isExportsPageFeatureGateEnabledStatsig: boolean;
}) => {
	const adminContent = {
		label: "Admin Tools",
		to: "/admin",
		subLinks: [
			// {
			// label: "The KF Report",
			// to: "/admin/knightfrankreport"
			// },
			{
				label: "Facility Management",
				to: "/admin/listings"
			},
			{
				label: "Market Management",
				to: "/admin/markets"
			},
			{
				label: "Tag Management",
				to: "/admin/tags"
			},
			{
				label: "Certificate Management",
				to: "/admin/certificates"
			},
			{
				label: "Company Management",
				to: "/admin/companies/management"
			},
			{
				label: "Research Management",
				to: "/admin/research-management"
			},
			{
				label: "Shared Datasets",
				to: "/admin/datasets"
			}
		],
		// buttons: [
		// {
		// label: "Send Notification To Users",
		// onClick: () => setIsModalOpen(true)
		// }, {
		// label: "Send Patch Update",
		// onClick: () => setIsPatchUpdateModalOpen(true)
		// }
		// ],
		toggled: false
	};
	
	if (isDcbCompanyAdmin || isSuperAdmin) {
		adminContent.subLinks.push({
			label: "User Management",
			to: "/admin/users/management"
		});

		adminContent.subLinks.push({
			label: "API Keys",
			to: "/admin/api-keys"
		});
	}

	if (isSuperDev) {
		adminContent.subLinks.push({
			label: "Reset Cache",
			to: "/admin/cache"
		});
	}

	if (isExportsPageFeatureGateEnabledStatsig) {
		adminContent.subLinks.push({
			label: "Data Exports",
			to: "/admin/exports"
		});
	}

	return adminContent;
};

export const getAnalyticsContent = () => {
	const analyticsContent = {
		label: "Market Analytics",
		to: `/${urlEnums.analyticsUrl}`,
		subLinks: [
			{
				label: "Markets",
				to: `/${urlEnums.analyticsUrl}/markets`
			},
			{
				label: "Companies",
				to: `/${urlEnums.analyticsUrl}/companies`
			},
			{
				label: "Summaries",
				to: `/${urlEnums.analyticsUrl}/market-summaries`
			},
			{
				label: "My Activity Tracker",
				to: `/${urlEnums.analyticsUrl}/my-activity-tracker`
			}
		],
		toggled: false
	};

	return analyticsContent;
};

const getSuppliersContent = () => {
	const suppliersContent = {
		label: "Supplier Analytics",
		to: `/${urlEnums.suppliersUrl}`,
		subLinks: [
			{
				label: "Projects",
				to: `/${urlEnums.suppliersUrl}/projects`
			},
			{
				label: "Markets & Companies",
				to: `/${urlEnums.suppliersUrl}/market-companies`
			},
			{
				label: "My Saved Projects",
				to: `/${urlEnums.suppliersUrl}/my-saved-projects`
			}
		],
		toggled: false
	};

	return suppliersContent;
};

const getResolveContent = (isUserCompanyAdmin: boolean) => {
	const subLinks = [
		{
			label: "Site Map",
			to: `/${urlEnums.projectPlannerUrl}/search?searchType=${resolveConstant}&view=map`
		},
		{
			label: "Facilities",
			to: `/${urlEnums.projectPlannerUrl}/listings`
		},
		{
			label: "Projects",
			to: `/${urlEnums.projectPlannerUrl}/projects`
		},
		{
			label: "Contacts",
			to: `/${urlEnums.projectPlannerUrl}/contacts`
		},
		{
			label: "Datasets",
			to: `/${urlEnums.projectPlannerUrl}/datasets`
		}
	];

	if (isUserCompanyAdmin) {
		subLinks.push(
			{
				label: "Templates",
				to: `/${urlEnums.projectPlannerUrl}/templates`
			}
		);
	}

	const resolveContent = {
		label: resolveConstant,
		to: `/${urlEnums.projectPlannerUrl}`,
		subLinks,
		toggled: false
	};

	return resolveContent;
};

const getSearchContent = () => {
	const searchContent = {
		label: "Map Search",
		to: "/search"
	};

	return searchContent;
};

const getMarketReportsSubLinks = (kfReports) => {
	const subLinks = [];

	for (let index = 0; index < kfReports.length; index += 1) {
		const report = kfReports[index];

		if (index === 0) {
			subLinks.push({
				label: "Knight Frank Reports"
			});
			
			subLinks.push({
				label: `Q${report.quarter} ${report.year}`,
				to: `/knight-frank-data-centres-report/Q${report.quarter}-${report.year}`
			});
		} else {
			subLinks.push({
				label: `Q${report.quarter} ${report.year}`,
				to: `/knight-frank-data-centres-report/Q${report.quarter}-${report.year}`
			});
		}
	}

	return subLinks;
};

const getNewsContent = (kfReports) => {
	const KfSubLinks = getMarketReportsSubLinks(kfReports);

	const resolveContent = {
		label: "Resources",
		to: { pathname: "https://dcbyte.com/news-blogs/" },
		subLinks: [
			{
				label: "Market Spotlight",
				to: { pathname: "https://www.dcbyte.com/market-spotlights/?utm_campaign=App%20-%20Internal&utm_source=Analytics%20App&utm_content=marketspotlights", target: "_blank" },
				target: "_blank",
				rel: "noopener noreferrer"
			},
			{
				label: "Market Infographics",
				to: { pathname: "https://www.dcbyte.com/market-infographics/?utm_campaign=App%20-%20Internal&utm_source=Analytics%20App&utm_content=marketinfographics", target: "_blank" },
				target: "_blank",
				rel: "noopener noreferrer"
			},
			{
				label: "News & Blogs",
				to: { pathname: "https://www.dcbyte.com/news-blogs/?utm_campaign=App%20-%20Internal&utm_source=Analytics%20App&utm_content=newsblogs", target: "_blank" },
				target: "_blank",
				rel: "noopener noreferrer"
			},
			...KfSubLinks
		],
		toggled: false
	};

	return resolveContent;
};

export const getNavLinks = ({
	activeUser,
	// setIsModalOpen,
	// setIsPatchUpdateModalOpen,
	kfReports,
	isExportsPageFeatureGateEnabledStatsig
}) => {
	if (!activeUser) {
		return [getSearchContent(), getNewsContent(kfReports)];
	}

	const { roles } = activeUser;

	const isUserCompanyAdmin = activeUser.rights === userRightsEnum.COMPANYADMIN;
	const isAdmin = authorize(roles, authorizationRoles.ADMIN);
	const isSuperAdmin = authorize(roles, authorizationRoles.SUPERADMIN);
	const isDcbCompanyAdmin = isUserCompanyAdmin && isAdmin;

	const superAdmins = ["devsuperadmin@dcbyte.com", "tetiana@dcbyte.com"];
	const isSuperDev = authorize(roles, authorizationRoles.SUPERADMIN) && superAdmins.includes(activeUser.email);

	const dashboardContent = getDashboardContent();

	const adminContent = getAdminContent({
		isSuperDev, isSuperAdmin, isDcbCompanyAdmin, isExportsPageFeatureGateEnabledStatsig
	});

	const analyticsContent = getAnalyticsContent();

	const suppliersContent = getSuppliersContent();

	const resolveContent = getResolveContent(isUserCompanyAdmin);

	const searchContent = getSearchContent();

	const newsContent = getNewsContent(kfReports);

	const navLinks: NavLinksInterface[] = [dashboardContent];

	navLinks.push(searchContent);

	const userHasMarketAnalyticsAccess = !!activeUser && checkForMarketAnalyticsAccess(activeUser);

	const userHasSiteSelectorAccess = !!activeUser && checkForSiteSelectorAccess(activeUser);

	const userHasSupplierAnalyticsAccess = !!activeUser && checkForSupplierAnalyticsAccess(activeUser);

	if (userHasMarketAnalyticsAccess) {
		navLinks.push(analyticsContent);
	}

	if (userHasSupplierAnalyticsAccess) {
		navLinks.push(suppliersContent);
	}

	if (userHasSiteSelectorAccess) {
		navLinks.push(resolveContent);
	}

	if (isAdmin || isSuperAdmin) {
		navLinks.push(adminContent);
	}

	navLinks.push(newsContent);

	return navLinks;
};
